import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Star } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const EMPTY = { name: "", project_type: "", text: "", rating: 5, published: true, order: 0 };
const inputCls = "w-full border border-line bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold";
const labelCls = "block font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-1.5";

export default function ReviewsTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name.trim() || !editing.text.trim()) { toast.error("Customer name and review text are required"); return; }
    setSaving(true);
    const payload = { ...editing, rating: Number(editing.rating) || 5, order: Number(editing.order) || 0 };
    try {
      if (editing.id) {
        await api.put(`/admin/reviews/${editing.id}`, payload);
        toast.success("Review updated");
      } else {
        await api.post("/admin/reviews", payload);
        toast.success("Review added");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setItems(items.filter((x) => x.id !== id));
      toast.success("Review deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (items === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-xl text-ink">Reviews <span className="text-slate2 text-sm font-sans font-normal">({items.length})</span></h2>
        {!editing && (
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="review-new-btn" className="inline-flex items-center gap-2 bg-ink text-white font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-charcoal transition-colors">
            <Plus className="w-4 h-4 text-gold" /> Add Review
          </button>
        )}
      </div>

      {editing && (
        <div data-testid="review-editor" className="bg-white border border-line p-5 md:p-7 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-semibold text-lg text-ink">{editing.id ? "Edit Review" : "Add Review"}</h3>
            <button onClick={() => setEditing(null)} data-testid="review-editor-close" className="p-1.5 text-slate2 hover:text-ink"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Customer Name*</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="review-name-input" className={inputCls} placeholder="e.g. Sarah & Mike T." />
            </div>
            <div>
              <label className={labelCls}>Project Type</label>
              <input value={editing.project_type} onChange={(e) => setEditing({ ...editing, project_type: e.target.value })} data-testid="review-type-input" className={inputCls} placeholder="e.g. Kitchen Renovation" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Review Text*</label>
              <textarea rows={4} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} data-testid="review-text-input" className={`${inputCls} resize-y`} placeholder="Paste the genuine customer review here." />
            </div>
            <div>
              <label className={labelCls}>Rating</label>
              <select value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} data-testid="review-rating-select" className={inputCls}>
                {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Display Order</label>
              <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="review-order-input" className={inputCls} />
            </div>
            <label className="sm:col-span-2 inline-flex items-center gap-2.5 text-sm text-ink cursor-pointer">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="review-published-checkbox" className="w-4 h-4 accent-[#C88E38]" />
              Published (visible on the website)
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="review-save-btn" className="mt-5 inline-flex items-center gap-2 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 disabled:opacity-60 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing.id ? "Save Changes" : "Add Review"}
          </button>
        </div>
      )}

      {items.length === 0 && !editing ? (
        <div data-testid="reviews-admin-empty" className="bg-white border border-line px-8 py-16 text-center">
          <Star className="w-10 h-10 text-line mx-auto mb-4" />
          <p className="font-display font-semibold text-lg text-ink">No reviews yet</p>
          <p className="text-sm text-slate2 mt-2">Add genuine customer reviews here — they'll appear on the website.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-admin-list">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-line px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "text-gold fill-gold" : "text-line"}`} />)}
              </div>
              <div className="flex-1 min-w-[200px]">
                <p className="font-display font-semibold text-sm text-ink">{r.name} {!r.published && <span className="font-mono text-[9px] uppercase tracking-wider text-orange-600 ml-2">Draft</span>}</p>
                <p className="text-xs text-slate2 truncate">{r.text}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing({ ...r })} data-testid={`admin-review-edit-${r.id}`} className="inline-flex items-center gap-1.5 text-xs border border-line px-3 py-1.5 hover:border-ink transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
                <button onClick={() => remove(r.id)} data-testid={`admin-review-delete-${r.id}`} className="inline-flex items-center gap-1.5 text-xs text-red-600 border border-line px-3 py-1.5 hover:border-red-300 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
